import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import { ErrorApi } from '../api/errores'

/**
 * Vista de calendario MENSUAL de repartos (referencia HEKKIO: rejilla del mes con
 * píldoras por día + leyenda). Hermano del `useCalendarioSemana`, pero agrupando
 * por fecha ISO (YYYY-MM-DD) en vez de por día de la semana, porque el mes cruza
 * varias semanas y cada casilla es un día concreto del calendario.
 *
 * Igual que el semanal, TOLERA que el endpoint aún no exista: hoy el backend no
 * expone `/api/agendamiento/calendario-mes`, así que un 4xx se traduce a un mes
 * vacío (la pantalla se monta y se ve) y solo un 5xx sube como error real.
 */

/** Un reparto agendado en un día concreto del mes. */
export interface RepartoMes {
  /** Identificador estable del reparto; sirve de `key`. */
  id: string
  /** Fecha del reparto en ISO local (YYYY-MM-DD). Es la clave de agrupación. */
  fecha: string
  /** Nombre del cliente. Lo imprescindible de leer. */
  cliente: string
  /** Ruta r1..r5 si se conoce (da el color de FONDO de la píldora). */
  ruta?: string | null
  /** Bono/pedido resumido, p. ej. "Bono 4 · 18.9L". Acompaña al nombre. */
  bono?: string | null
  /**
   * Estado del reparto. Da el MARCADOR (icono + estilo de borde), no el color de
   * fondo: fondo = ruta, marca = estado, así ambos datos se leen a la vez sin que
   * el color cargue nada solo. 'pendiente' por defecto si el backend no lo manda.
   */
  estado?: EstadoReparto | null
}

/** Los tres estados de un reparto en el ciclo semanal. */
export type EstadoReparto = 'confirmado' | 'pendiente' | 'aplazado'

/**
 * Repartos del mes agrupados por fecha ISO. Un `Record` disperso (solo los días
 * con carga): a diferencia de la semana, el mes tiene 28-31 días y la rejilla ya
 * sabe qué fechas pinta, así que basta con mirar si la clave existe.
 */
export type MesRepartos = Record<string, RepartoMes[]>

/** Ruta donde vivirá el calendario mensual cuando el backend lo exponga. */
const RUTA_CALENDARIO_MES = '/api/agendamiento/calendario-mes'

/**
 * Trae los repartos de un mes.
 *
 * @param mes Identificador de mes `YYYY-MM` (p. ej. `2026-09`). Obligatorio: la
 *   rejilla siempre sabe qué mes muestra, así que no hay "mes activo" implícito.
 */
export function useCalendarioMes(mes: string) {
  const api = useApi()

  return useQuery<MesRepartos, ErrorApi>({
    queryKey: ['calendario-mes', mes],
    queryFn: async ({ signal }) => {
      try {
        const datos = await api.pedir<MesRepartos>(RUTA_CALENDARIO_MES, {
          parametros: { mes },
          senal: signal,
        })
        return datos ?? {}
      } catch (error) {
        // El endpoint aún no existe: mes vacío y a seguir, como el semanal.
        if (error instanceof ErrorApi && error.estado < 500) return {}
        throw error
      }
    },
    retry: (intentos, error) => {
      if (error instanceof ErrorApi && error.estado < 500) return false
      return intentos < 2
    },
  })
}

/**
 * Construye la matriz de semanas del mes para pintar la rejilla (lunes primero).
 * Devuelve filas de 7 celdas; cada celda es una fecha `Date` (a medianoche
 * local). Incluye los días de relleno del mes anterior/siguiente para completar
 * la primera y última semana, marcados con `delMes:false` para atenuarlos.
 */
export interface CeldaMes {
  fecha: Date
  iso: string
  delMes: boolean
}

export function matrizDelMes(anio: number, mesIndice0: number): CeldaMes[][] {
  const primero = new Date(anio, mesIndice0, 1)
  // getDay(): 0=domingo..6=sabado. Queremos lunes primero: cuántos días retroceder.
  const desplazamiento = (primero.getDay() + 6) % 7
  const inicio = new Date(anio, mesIndice0, 1 - desplazamiento)

  const semanas: CeldaMes[][] = []
  const cursor = new Date(inicio)
  // 6 filas cubren cualquier mes (máximo 6 semanas visibles).
  for (let fila = 0; fila < 6; fila++) {
    const semana: CeldaMes[] = []
    for (let col = 0; col < 7; col++) {
      semana.push({
        fecha: new Date(cursor),
        iso: isoLocal(cursor),
        delMes: cursor.getMonth() === mesIndice0,
      })
      cursor.setDate(cursor.getDate() + 1)
    }
    semanas.push(semana)
    // Si ya pasamos el mes y la fila está completa, no pintamos filas de más.
    if (cursor.getMonth() !== mesIndice0 && cursor > new Date(anio, mesIndice0 + 1, 0)) {
      if (fila >= 4) break
    }
  }
  return semanas
}

/** Fecha → 'YYYY-MM-DD' en hora LOCAL (el día del reparto es el de quien mira). */
export function isoLocal(fecha: Date): string {
  const y = fecha.getFullYear()
  const m = String(fecha.getMonth() + 1).padStart(2, '0')
  const d = String(fecha.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
